import { stateAbbreviationMap, stateCenters } from '@/utils/formatters'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import { BOX_TRUCK_MAP_LOADS_MIN_ZOOM, BOX_TRUCK_MAP_STATE_MARKER_MAX_ZOOM } from '../constants/boxTruckMap'
import type { StateActivity } from '../types/boxTruckMap'

const STATE_CODE_BY_NAME = Object.entries(stateAbbreviationMap).reduce<Record<string, string>>((codes, [code, name]) => {
    codes[name.toLowerCase()] = code
    return codes
}, {})

export const getStateName = (value: string): string | undefined => {
    const cleaned = value.trim().replace(/^US-/i, '')
    const code = STATE_CODE_BY_NAME[cleaned.toLowerCase()]
    return stateAbbreviationMap[cleaned.toUpperCase()] ?? (code ? stateAbbreviationMap[code] : undefined)
}

export const getStateCode = (value: string | null): string | undefined => {
    if (!value) return undefined
    const fullName = getStateName(value)
    const codeFromName = fullName ? STATE_CODE_BY_NAME[fullName.toLowerCase()] : undefined
    const rawCode = value.trim().toUpperCase()
    return codeFromName ?? (/^[A-Z]{2}$/.test(rawCode) ? rawCode : undefined)
}

/** A US state code for a reverse-geocoded state (code or name), or null when it is not one of ours. */
export const getUsStateCode = (state: string | null | undefined): string | null => {
    const stateCode = getStateCode(state ?? null)
    return stateCode && getStateName(stateCode) ? stateCode : null
}

export const shouldResolveMapLoadState = (zoom: number): boolean => zoom >= BOX_TRUCK_MAP_LOADS_MIN_ZOOM && zoom <= BOX_TRUCK_MAP_STATE_MARKER_MAX_ZOOM

export const buildStateActivities = (loadsByState: Array<{ state: string; count: number }>, drivers: BoxTruckMapDriver[]): StateActivity[] => {
    const metrics = new Map<string, { loads: number | null; drivers: number }>()

    for (const row of loadsByState) {
        const name = getStateName(row.state)
        if (!name) continue
        const current = metrics.get(name) ?? { loads: null, drivers: 0 }
        metrics.set(name, { ...current, loads: row.count })
    }

    for (const driver of drivers) {
        const name = driver.state ? getStateName(driver.state) : undefined
        if (!name) continue
        const current = metrics.get(name) ?? { loads: null, drivers: 0 }
        metrics.set(name, { ...current, drivers: current.drivers + 1 })
    }

    const states: StateActivity[] = []
    for (const [name, counts] of metrics) {
        const code = STATE_CODE_BY_NAME[name.toLowerCase()]
        const center = stateCenters[name]
        if (!code || !center) continue
        states.push({ name, code, ...counts, position: { lng: center[0], lat: center[1] } })
    }

    return states.sort((first, second) => second.drivers - first.drivers || first.name.localeCompare(second.name))
}

export const getDriverSearchLabel = (driver: BoxTruckMapDriver): string => {
    const name = driver.name?.trim() || 'Unnamed driver'
    const truckNumber = driver.truckNumber ? `Truck #${driver.truckNumber.replace(/^#/, '')}` : null
    return [name, truckNumber, driver.equipment].filter(Boolean).join(' · ')
}

export const findMatchingDrivers = (drivers: BoxTruckMapDriver[], query: string, limit: number): BoxTruckMapDriver[] => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return []

    const matches: BoxTruckMapDriver[] = []
    for (const driver of drivers) {
        const values = [driver.name, driver.truckNumber, driver.equipment, driver.city, driver.state]
        if (!values.some((value) => value?.toLowerCase().includes(normalizedQuery))) continue
        matches.push(driver)
        if (matches.length === limit) break
    }
    return matches
}
